import * as React from 'react';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { OptionType } from '../FlexySelect';

export type BaseItemProps = {
    label: string;
    baseline?: string;
    value: OptionType['value'];
    isChecked?: boolean;
    isDisabled?: boolean | ((id: number) => boolean);
    onChange?: (value: BaseItemProps['value'], checked: boolean) => void;
};

export type SelectItemProps = BaseItemProps & {
    icon?: string;
    fullWidth?: boolean;
    design?: 'block' | 'inline' | 'basic';
};

export const SelectItem: React.FunctionComponent<SelectItemProps> = function ({
    label,
    baseline,
    value,
    icon,
    fullWidth = true,
    design = 'inline',
    isChecked = false,
    isDisabled = false,
    onChange = () => {},
}) {
    const { palette } = useTheme();

    const changeCheck = () => {
        onChange(value, !isChecked);
    };

    let flexDirection: React.CSSProperties;
    let cssChecked: React.CSSProperties = {};
    let cssIconBox: React.CSSProperties;
    let cssDisabled: React.CSSProperties = {};

    let iconWithShape: boolean = false;
    const colorIcon = palette.text.gris70;

    if (design === 'inline') {
        flexDirection = {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            height: '48px',
            padding: '4px 16px 4px 16px',
        };
        cssIconBox = {
            fontSize: '18px',
            marginRight: '5px',
        };
    } else if (design === 'block') {
        iconWithShape = true;
        flexDirection = {
            flexDirection: 'column',
            height: '149px',
            justifyContent: 'space-around',
            padding: '10px',
            textAlign: 'center',
        };
        cssIconBox = {
            marginTop: '15px',
            fontSize: '28px',
        };
    } else {
        flexDirection = {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            height: '48px',
            padding: '4px 16px 4px 16px',
        };
        cssIconBox = {
            fontSize: '18px',
            marginRight: '5px',
        };
    }

    if (isDisabled) {
        cssDisabled = {
            borderColor: palette.divider,
            borderWidth: '1px',
            borderStyle: 'solid',
            background: palette.action.disabledBackground,
            cursor: 'not-allowed',
        };
    } else if (isChecked) {
        cssChecked = {
            borderColor: palette.primary.main,
            borderWidth: '1px',
            borderStyle: 'solid',
            background: alpha(palette.primary.main, 0.15),
        };
    } else {
        cssChecked = {
            borderColor: palette.divider,
            borderWidth: '1px',
            borderStyle: 'solid',
            background: palette.background.paper,
        };
    }

    const iconBox = (
        <Typography
            component="span"
            style={{
                ...cssIconBox,
            }}
        >
            <FaOptionIcon
                icon={icon as IconName}
                size="xs"
                color={colorIcon}
                withShape={iconWithShape}
            />
        </Typography>
    );

    const genericStyle = {
        display: 'flex',
        gap: 1,
        flexDirection: 'column',
        justifyContent: 'center',
    };

    return (
        <Box
            sx={{
                width: fullWidth ? '100%' : 'fit-content',
                display: 'flex',
                gap: 1,
                borderRadius: '15px',
                cursor: 'pointer',
                position: 'relative',
                alignItems: 'center',
                boxSizing: 'border-box',
                transition: 'background-color ease-in-out 150ms, border-color ease-in-out 150ms',
                ':hover': {
                    backgroundColor: alpha(palette.primary.main, 0.08),
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: palette.primary.main,
                },
                ...flexDirection,
                ...cssDisabled,
                ...cssChecked,
            }}
            className={isChecked ? 'selectItem-checked' : 'selectItem-unchecked'}
            onClick={() => {
                if (!isDisabled) {
                    changeCheck();
                }
            }}
        >
            {icon ? iconBox : null}
            <Box
                style={{
                    display: 'flex',
                    gap: 1,
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    variant="l2dcaption"
                    color={palette.text.gris70}
                    sx={
                        design === 'basic'
                            ? {
                                  ...genericStyle,
                                  whiteSpace: 'nowrap',
                              }
                            : { ...genericStyle }
                    }
                >
                    {label}
                </Typography>
                {baseline && design !== 'basic' ? (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            lineHeight: '16px',
                            marginTop: '5px',
                        }}
                        color="text.secondary"
                    >
                        {baseline}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
};
